import React, {FC, useState} from "react"

import {FormattedMessage, FormattedCurrency, injectIntl, InjectIntl, FormattedNumber} from "@irati/utils/lang"
import {withTheme, WithDefaultTheme} from "styled-components"
import {PaymentCurrencyType, WalletEntityType} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {H2, H6, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import {Checkbox, ValidationButton} from "@irati/ui/src/FormItem"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import authentication from "@irati/auth"
import Validation from "@irati/utils/validation"

import {Card} from "@irati/ui/src/Layout"
import {StepNames} from "../../../enums"
import {DistributeRewardImportType} from "../../../enums/DistributeRewardTypes"
import {DistributeRewardState} from "../../../stores/DistributeReward/actions"

interface Props extends InjectIntl, WithDefaultTheme {
  onSubmit: () => void,
  payload: DistributeRewardState,
  handlePrevStep: () => void,
  buttonText: string
}

const DistributeRewardSummaryStep2: FC<Props> = ({theme, onSubmit, payload, handlePrevStep, buttonText, formatMessage}) => {
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [submit, setSubmit] = useState<Promise<any> | null>(null)

  const confirmSendReward = () => {
    setIsChecked((current) => !current)
  }

  const handleSubmit = () => {
    setSubmit(onSubmit())
  }

  const totUsers =
    payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV ? payload.userEmails.length : payload.destinationUsers.length
  const totalToPay = payload.pointsPerUser * totUsers || 0

  return (
    <>
      <Card flush>
        <H6 paddingLeft={3} paddingTop={4} paddingBottom={4}>
          <FormattedMessage id="wallet.reward.admin.summary.title" />
        </H6>
        <Flex style={{background: theme.colors.utility.highlight}} justifyContent="center" paddingTop={2} paddingBottom={2}>
          <Flex direction="column" alignItems="center">
            <Label small marginBottom={0.5}>
              <FormattedMessage id="wallet.reward.intro.available" />
            </Label>
            <WalletBallance
              variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
              {(apollo) => {
                return <FormattedCurrency value={apollo.data.wallet.amount} currency={PaymentCurrencyType.Rp} />
              }}
            </WalletBallance>
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" alignItems="center" padding={3}>
          <Label large>
            <FormattedMessage id="wallet.reward.admin.card.topup.totalToPay" />
          </Label>
          <Flex alignItems="center">
            <Icon icon={IconTypes.rewardPoints} size={32} />
            <H2 marginLeft={1}>
              <FormattedNumber value={totalToPay} />
            </H2>
          </Flex>
        </Flex>

        <Flex paddingLeft={3} paddingRight={3}>
          <Checkbox
            name="rewardAccord"
            checked={isChecked}
            validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}
            onChange={confirmSendReward}>
            <FormattedMessage id="wallet.reward.admin.distribute.checkbox.title" />
          </Checkbox>
        </Flex>

        <Flex justifyContent="space-between" paddingLeft={3} paddingRight={3} paddingBottom={3}>
          {!location.pathname.includes(StepNames.Recipients) && (
            <Button onClick={handlePrevStep} marginRight>
              <FormattedMessage id="platform.button.back" />
            </Button>
          )}
          <ValidationButton full primary onClick={handleSubmit} load={submit}>
            <FormattedMessage id={buttonText} />
          </ValidationButton>
        </Flex>
      </Card>
    </>
  )
}

export default injectIntl(withTheme(DistributeRewardSummaryStep2))
